import { useMemo, useRef, useState } from 'react';
import { CUSTOM_FIELD_NAME_PATTERN, type CustomField, type CustomFieldKind, type FeederFieldConfig } from '@kkp-fraud/shared';
import { isStandardField } from '../fields';
import { t } from '../i18n';
import { Dialog } from './Dialog';
import { Field, useFieldErrors } from './Field';

/**
 * Wave 6 — bind a raw feeder key as a custom field, or edit one (docs/specs/wave6/
 * field-bindings.md). The same checks the engine makes (`custom_field_name`,
 * `custom_field_clash`) are shown here so Save is not a round trip to find them; the engine's
 * validation stays the authority when the draft is released.
 */

const KINDS: readonly CustomFieldKind[] = ['string', 'number', 'boolean'];

/** A key as a suggested name: lower snake case, starting with a letter, at most 40 characters. */
export function suggestedName(key: string): string {
  const snake = key.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^[^a-z]+/, '');
  return snake.slice(0, 40);
}

export type CustomFieldProblem = 'name' | 'clash';

/** What stops `field` from being saved next to `others` (the list without the one being edited). */
export function customFieldProblems(field: CustomField, others: readonly CustomField[]): Partial<Record<CustomFieldProblem, true>> {
  const out: Partial<Record<CustomFieldProblem, true>> = {};
  if (!CUSTOM_FIELD_NAME_PATTERN.test(field.name)) out.name = true;
  else if (isStandardField(field.name) || others.some((o) => o.name === field.name)) out.clash = true;
  return out;
}

export function CustomFieldDialog({
  initial,
  from,
  rows,
  others,
  onSave,
  onClose,
}: {
  /** The binding being edited; absent = a new one. */
  initial?: CustomField;
  /** The feeder key it reads — fixed: a binding is made from its row. */
  from: string;
  /** The feeder payload in force. */
  rows: readonly FeederFieldConfig[];
  /** Every other custom field of the document (for the clash check). */
  others: readonly CustomField[];
  onSave: (field: CustomField) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<CustomField>(() => ({
    name: initial?.name ?? suggestedName(from),
    kind: initial?.kind ?? 'string',
    from,
    ...(initial?.description ? { description: initial.description } : {}),
  }));
  const errors = useFieldErrors();
  const body = useRef<HTMLDivElement>(null);
  const problems = useMemo(() => customFieldProblems(form, others), [form, others]);
  const patch = (p: Partial<CustomField>) => setForm((prev) => ({ ...prev, ...p }));

  const save = () => {
    if (!errors.attempt(Object.keys(problems).length === 0, body.current?.closest('dialog'))) return;
    const description = form.description?.trim();
    const { description: _drop, ...rest } = form;
    onSave(description ? { ...rest, name: form.name.trim(), description } : { ...rest, name: form.name.trim() });
  };

  const nameError = problems.name ? t('fields.custom.problem.name') : problems.clash ? t('fields.custom.problem.clash') : undefined;
  const row = rows.find((r) => r.key === form.from);

  return (
    <Dialog
      title={initial ? t('fields.custom.dialog.edit.title', { name: initial.name }) : t('fields.custom.dialog.new.title')}
      testid="custom-field-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" data-testid="custom-field-save" onClick={save}>
            {t('fields.feeder.save.action')}
          </button>
        </>
      }
    >
      <div className="form-grid" ref={body}>
        <Field label={t('fields.custom.from.label')} required testid="custom-field-from">
          <input type="text" className="mono-input" value={from} readOnly />
        </Field>
        <Field label={t('fields.custom.name.label')} required hint={t('fields.custom.name.hint')} name="name" form={errors} error={errors.show('name', nameError)} testid="custom-field-name">
          <input type="text" className="mono-input" spellCheck={false} maxLength={40} value={form.name} onChange={(e) => patch({ name: e.target.value })} />
        </Field>
        <Field label={t('fields.custom.kind.label')} required hint={t('fields.custom.kind.hint')} testid="custom-field-kind">
          <select value={form.kind} onChange={(e) => patch({ kind: e.target.value as CustomFieldKind })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`fieldkind.${k}.label`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('fields.custom.description.label')} wide testid="custom-field-description">
          <input type="text" value={form.description ?? ''} placeholder={row?.name} onChange={(e) => patch({ description: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}
